import { Box, Container, useMediaQuery, Fab, Tooltip } from '@mui/material';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import FeedbackIcon from '@mui/icons-material/Feedback';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { LoadingScreen } from '../components/common/LoadingScreen';
import { UpdatingDataChip } from '../components/common/UpdatingDataChip';
import { RefreshBar } from '../components/common/RefreshBar';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from 'react-simple-maps';
import nzGeoData from '../data/nz-geo.json';

// Import extracted data and hooks
import { NZ_AIRPORTS_DATA } from '../data/locations';
import { useAvailableLocations } from '../hooks/useAvailableLocations';
import { useMapTooltip } from '../hooks/useMapTooltip';
import { useHourlyTimelineData } from '../hooks/timeline/useHourlyTimelineData';
import { useFogStatus } from '../hooks/map/useFogStatus';
import { useAirportSelection } from '../hooks/map/useAirportSelection';
import { useAirportStatuses } from '../hooks/map/useAirportStatuses';

// Import map constants
import {
  MAP_SCALES,
  NZ_MAP_CENTER,
  MAP_ZOOM,
  GEOGRAPHY_STYLE,
  MAP_COMPONENT_STYLES
} from '../constants/mapConstants';

// Import styles
import * as styles from '../styles/MapPageStyles';

// Import map components
import StandaloneLegend from '../components/map/StandaloneLegend';
import HorizontalLegend from '../components/map/HorizontalLegend';
import MapTooltip from '../components/map/MapTooltip';
import { TimelineDrawer } from '../components/map/TimelineDrawer';
import AirportMarker from '../components/map/AirportMarker';

// Import common components
import StatusAlert from '../components/common/StatusAlert';
import { FeedbackModal } from '../components/feedback/FeedbackModal';

// Import utilities
import { formatVisibility } from '../utils/visibilityFormatters';

// Use imported AIRPORTS from data file
const AIRPORTS = NZ_AIRPORTS_DATA;

// Helper to check if airport is selected
function isAirportSelected(airport: { code: string }, selectedAirports: Array<{ code: string } | null>): boolean {
  return selectedAirports.some(selected => selected?.code === airport.code);
}

// Helper to get map scale based on viewport
function getMapScale(
  isMobilePortrait: boolean,
  isTabletLandscape: boolean,
  isTablet: boolean,
  isMobileLandscape: boolean
): number {
  if (isMobilePortrait) return MAP_SCALES.mobilePortrait;
  if (isTabletLandscape) return MAP_SCALES.tabletLandscape;
  if (isTablet) return MAP_SCALES.tabletPortrait;
  if (isMobileLandscape) return MAP_SCALES.mobileLandscape;
  return MAP_SCALES.desktop;
}

// Constant empty array to maintain stable reference across renders
const EMPTY_LOCATIONS: number[] = [];

export default function MapPage() {
  const navigate = useNavigate();
  const isTablet = useMediaQuery('(min-width:768px) and (max-width:1023px)');
  const isMobileLandscape = useMediaQuery('(orientation: landscape) and (max-width: 1023px)');
  const isTabletLandscape = useMediaQuery('(orientation: landscape) and (min-width: 768px) and (max-width: 1199px)');
  const isMobilePortrait = useMediaQuery('(orientation: portrait) and (max-width: 767px)');
  const isPortrait = useMediaQuery('(orientation: portrait)'); // All portrait orientations

  const [loaded, setLoaded] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [, setRefreshTick] = useState(0); // Force re-render for time display
  const [drawerOpen, setDrawerOpen] = useState(false); // Sidebar drawer state (default closed)

  // Fetch available locations - single source of truth
  const { availableLocations, hasFullNZAccess, loading: accessLoading } = useAvailableLocations();

  // Extract NZ location IDs only — the map view only shows NZ airports
  const dataAirports = useMemo(() => {
    if (accessLoading) return EMPTY_LOCATIONS;
    return availableLocations
      .filter(loc => loc.country_code === 'NZ')
      .map(loc => loc.id);
  }, [availableLocations, accessLoading]);

  // Redirect users without full NZ access to timeline view
  // Map view is designed for users with access to all NZ airports
  useEffect(() => {
    // Wait for access check to complete before redirecting
    if (!accessLoading && !hasFullNZAccess) {
      navigate('/', { replace: true });
    }
  }, [hasFullNZAccess, accessLoading, navigate]);

  const {
    tooltipContent,
    handleMarkerMouseEnter,
    handleMarkerMouseLeave
  } = useMapTooltip();

  // Use hourly timeline data for map view
  const {
    timelineData: airportTimelineData,
    loading,
    hasDataError,
    lastUpdated,
    refresh,
    refreshCount
  } = useHourlyTimelineData(dataAirports, null, 5);

  // Use airport statuses hook to process timeline data
  const {
    airportColors,
    airportStatuses
  } = useAirportStatuses({
    airportTimelineData,
    dataAirports
  });

  // Use airport selection hook
  const {
    selectedAirports,
    handleAirportClick
  } = useAirportSelection({
    airportStatuses,
    dataAirports,
    refreshCount
  });



  useEffect(() => {
    // Set loaded to true after component mounts to avoid SSR issues
    setLoaded(true);
  }, []);

  // Update the time display periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTick(tick => tick + 1); // Force re-render to update time display
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Filter function that disables all zoom and pan events
  const disableAllInteractions = () => false;

  // Use the fog status hook to calculate overall fog status
  const fogStatus = useFogStatus(airportColors, dataAirports);

  // Check if we have existing data for silent refresh
  const hasExistingData = Object.keys(airportTimelineData).length > 0;

  // Show loading screen while checking access, redirecting, or loading initial data
  // Always show loading until we have data, even if loading state is briefly false
  const isInitialLoad = accessLoading || (!hasFullNZAccess && !accessLoading) || !hasExistingData;

  if (isInitialLoad) {
    return <LoadingScreen message="Loading forecast data..." />;
  }

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ px: { xs: 1.5, sm: 3, md: 3.5 } }}  // Align with AppBar logo and menu icons
    >
      <Box sx={styles.getContainerWithRefreshBarStyles(hasExistingData)}>

        {/* Subtle refresh indicator when updating with existing data */}
        <UpdatingDataChip show={loading && hasExistingData} />

        {/* Internal box with margins - show when we have data OR when not loading */}
        {(hasExistingData || !loading) && (
          <Box sx={styles.internalBoxStyles}>
            {/* Status bar - full width constrained by internal box */}
            <Box sx={styles.statusBarContainerStyles}>
              <StatusAlert
                fogStatus={fogStatus}
                hasDataError={hasDataError}
                isLoading={loading && !hasExistingData}
              />
            </Box>

            {/* Content div - flexbox with legend on left/map on right (landscape) or map top/legend bottom (portrait) */}
            <Box sx={styles.getContentDivStyles(isPortrait)}>
              {/* Standalone legend on the left (landscape only) */}
              {!isPortrait && <StandaloneLegend formatVisibility={formatVisibility} />}

              {/* Map container */}
              <Box sx={styles.getMapContainerStyles(isPortrait)}>
                {loaded && (
                  <ComposableMap
                    projection="geoMercator"
                    projectionConfig={{
                      scale: getMapScale(isMobilePortrait, isTabletLandscape, isTablet, isMobileLandscape),
                      center: NZ_MAP_CENTER
                    }}
                    style={MAP_COMPONENT_STYLES.composableMap}
                  >
                    <ZoomableGroup
                      zoom={MAP_ZOOM.level}
                      center={MAP_ZOOM.center}
                      disablePanning={true}
                      disableZooming={true}
                      filterZoomEvent={disableAllInteractions}
                    >
                      <Geographies geography={nzGeoData}>
                        {({ geographies }) => {
                          // Filter to only get New Zealand
                          const nzGeo = geographies.filter(geo =>
                            geo.properties.ISO_A3 === "NZL" ||
                            geo.properties.ADM0_A3 === "NZL" ||
                            (geo.properties.NAME && geo.properties.NAME.includes("Zealand"))
                          );
                          return nzGeo.map(geo => (
                            <Geography
                              key={geo.rsmKey}
                              geography={geo}
                              fill={GEOGRAPHY_STYLE.fill}
                              stroke={GEOGRAPHY_STYLE.stroke}
                              strokeWidth={GEOGRAPHY_STYLE.strokeWidth}
                              style={MAP_COMPONENT_STYLES.geographyInteraction}
                            />
                          ));
                        }}
                      </Geographies>

                      {/* Add airport markers */}
                      {AIRPORTS.map((airport) => {
                        // Get both actual and forecast colors
                        const status = airportStatuses[airport.id] || { actualColor: airport.color, forecastColor: airport.color };

                        return (
                          <AirportMarker
                            key={airport.code}
                            airport={airport}
                            status={status}
                            isSelected={isAirportSelected(airport, selectedAirports)}
                            onMouseEnter={(e) => handleMarkerMouseEnter(airport, e)}
                            onMouseLeave={handleMarkerMouseLeave}
                            onClick={() => {
                              handleAirportClick(airport);
                              setDrawerOpen(true);
                            }}
                          />
                        );
                      })}
                    </ZoomableGroup>
                  </ComposableMap>
                )}

                {/* Custom tooltip */}
                <MapTooltip
                  tooltipContent={tooltipContent}
                  airportStatuses={airportStatuses}
                  formatVisibility={formatVisibility}
                />

                {/* Toggle button for drawer */}
                <Fab
                  size="medium"
                  onClick={() => setDrawerOpen(!drawerOpen)}
                  sx={styles.drawerToggleButtonStyles}
                  aria-label={drawerOpen ? "Close sidebar" : "Open sidebar"}
                >
                  {drawerOpen ? <ChevronRightIcon sx={styles.drawerToggleIconStyles} /> : <ChevronLeftIcon sx={styles.drawerToggleIconStyles} />}
                </Fab>
              </Box>

              {/* Horizontal legend below map (portrait only) */}
              {isPortrait && <HorizontalLegend formatVisibility={formatVisibility} />}
            </Box>

            {/* Floating Feedback Button */}
            <Tooltip title="Let us know if something doesn't look right" placement="left">
              <Fab
                color="primary"
                size="medium"
                aria-label="feedback"
                onClick={() => setFeedbackModalOpen(true)}
                sx={styles.feedbackFabStyles}
              >
                <FeedbackIcon sx={styles.feedbackFabIconStyles} />
              </Fab>
            </Tooltip>
          </Box>
        )}

        {/* Refresh bar footer */}
        {hasExistingData && (
          <RefreshBar
            lastUpdated={lastUpdated}
            loading={loading}
            onRefresh={refresh}
          />
        )}
      </Box>

      {/* Timeline Drawer */}
      <TimelineDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        selectedAirports={selectedAirports}
        airportTimelineData={airportTimelineData}
        onViewAllTimelines={() => navigate('/')}
      />

      {/* Feedback Modal */}
      <FeedbackModal
        open={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
      />
    </Container>
  );
} 